import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getRelease, createRelease, updateRelease, getDSPServices, getSocialPlatforms, getMe, fetchLinksFromUrl, getTribeArtists, getTribeArtist } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { FaArrowLeft, FaPlus, FaTrash, FaCalendar, FaMusic, FaImage, FaYoutube, FaLink, FaMagic, FaSpinner, FaUsers } from "react-icons/fa";

export default function ReleaseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dspServices, setDspServices] = useState([]);
  const [socialPlatforms, setSocialPlatforms] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    artist: "",
    artwork_url: "",
    release_date: new Date().toISOString().split("T")[0],
    youtube_url: "",
    dsp_links: [],
    social_links: [],
    external_links: [],
  });

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [fetchUrl, setFetchUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [tribeArtists, setTribeArtists] = useState([]);
  const [selectedTribeArtist, setSelectedTribeArtist] = useState("");
  const [fetchingTribe, setFetchingTribe] = useState(false);
  const [loadingTribeArtists, setLoadingTribeArtists] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        await getMe();
        const [dspRes, socialRes] = await Promise.all([
          getDSPServices(),
          getSocialPlatforms(),
        ]);
        setDspServices(dspRes.data);
        setSocialPlatforms(socialRes.data);

        if (isEdit) {
          const releaseRes = await getRelease(id);
          setFormData({
            title: releaseRes.data.title,
            artist: releaseRes.data.artist,
            artwork_url: releaseRes.data.artwork_url,
            release_date: releaseRes.data.release_date,
            youtube_url: releaseRes.data.youtube_url || "",
            dsp_links: releaseRes.data.dsp_links || [],
            social_links: releaseRes.data.social_links || [],
            external_links: releaseRes.data.external_links || [],
          });
        }

        // Fetch Tribe artists
        try {
          const tribeRes = await getTribeArtists();
          setTribeArtists(tribeRes.data);
        } catch (e) {
          console.log("Tribe API not available");
        }
        setLoadingTribeArtists(false);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate("/admin");
        } else {
          toast.error("Failed to load data");
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id, isEdit, navigate]);

  const handleFetchLinks = async () => {
    if (!fetchUrl.trim()) {
      toast.error("Please paste a music URL first");
      return;
    }

    setFetching(true);
    try {
      const response = await fetchLinksFromUrl(fetchUrl);
      const data = response.data;

      // Update form with fetched data
      const updates = { ...formData };

      if (data.artwork_url) {
        updates.artwork_url = data.artwork_url;
      }
      if (data.title && !formData.title) {
        updates.title = data.title;
      }
      if (data.artist && !formData.artist) {
        updates.artist = data.artist;
      }
      if (data.dsp_links && data.dsp_links.length > 0) {
        // Merge with existing links, avoiding duplicates
        const existingServiceIds = formData.dsp_links.map((l) => l.service_id);
        const newLinks = data.dsp_links.filter(
          (l) => !existingServiceIds.includes(l.service_id)
        );
        updates.dsp_links = [...formData.dsp_links, ...newLinks];
      }

      setFormData(updates);
      setFetchUrl("");
      
      const fetchedCount = data.dsp_links?.length || 0;
      toast.success(`Fetched ${fetchedCount} streaming links${data.artwork_url ? " + artwork" : ""}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch links. Try a different URL.");
    } finally {
      setFetching(false);
    }
  };

  const handleFetchFromTribe = async () => {
    if (!selectedTribeArtist) {
      toast.error("Please select an artist from Tribe");
      return;
    }

    setFetchingTribe(true);
    try {
      const response = await getTribeArtist(selectedTribeArtist);
      const data = response.data;

      const updates = { ...formData };

      // Set artist name if empty
      if (data.name && !formData.artist) {
        updates.artist = data.name;
      }

      // Add social links (merge, avoid duplicates)
      if (data.social_links && data.social_links.length > 0) {
        const existingPlatformIds = formData.social_links.map((l) => l.platform_id);
        const newSocials = data.social_links.filter(
          (l) => !existingPlatformIds.includes(l.platform_id)
        );
        updates.social_links = [...formData.social_links, ...newSocials];
      }

      // Add DSP links (merge, avoid duplicates)
      if (data.dsp_links && data.dsp_links.length > 0) {
        const existingServiceIds = formData.dsp_links.map((l) => l.service_id);
        const newDsps = data.dsp_links.filter(
          (l) => !existingServiceIds.includes(l.service_id)
        );
        updates.dsp_links = [...formData.dsp_links, ...newDsps];
      }

      // Add Tribe landing page as external link
      if (data.tribe_landing_url) {
        const hasTribeLink = formData.external_links.some(
          (l) => l.name.toLowerCase().includes("tribe")
        );
        if (!hasTribeLink) {
          updates.external_links = [
            ...formData.external_links,
            { header: "Artist Page", name: "Tribe", url: data.tribe_landing_url }
          ];
        }
      }

      setFormData(updates);
      setSelectedTribeArtist("");

      const socialCount = data.social_links?.length || 0;
      const dspCount = data.dsp_links?.length || 0;
      toast.success(`Fetched ${socialCount} socials + ${dspCount} DSP links from Tribe!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch from Tribe");
    } finally {
      setFetchingTribe(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.artist || !formData.artwork_url || !formData.release_date) {
      toast.error("Please fill all required fields");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateRelease(id, formData);
        toast.success("Release updated!");
      } else {
        await createRelease(formData);
        toast.success("Release created!");
      }
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save release");
    } finally {
      setSaving(false);
    }
  };

  const addDSPLink = () => {
    setFormData({
      ...formData,
      dsp_links: [...formData.dsp_links, { service_id: dspServices[0]?.id || "", url: "" }],
    });
  };

  const removeDSPLink = (index) => {
    setFormData({
      ...formData,
      dsp_links: formData.dsp_links.filter((_, i) => i !== index),
    });
  };

  const updateDSPLink = (index, field, value) => {
    const updated = [...formData.dsp_links];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, dsp_links: updated });
  };

  const addSocialLink = () => {
    setFormData({
      ...formData,
      social_links: [...formData.social_links, { platform_id: socialPlatforms[0]?.id || "", url: "" }],
    });
  };

  const removeSocialLink = (index) => {
    setFormData({
      ...formData,
      social_links: formData.social_links.filter((_, i) => i !== index),
    });
  };

  const updateSocialLink = (index, field, value) => {
    const updated = [...formData.social_links];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, social_links: updated });
  };

  const addExternalLink = () => {
    setFormData({
      ...formData,
      external_links: [...formData.external_links, { header: "More Links", name: "", url: "" }],
    });
  };

  const removeExternalLink = (index) => {
    setFormData({
      ...formData,
      external_links: formData.external_links.filter((_, i) => i !== index),
    });
  };

  const updateExternalLink = (index, field, value) => {
    const updated = [...formData.external_links];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, external_links: updated });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse">
          <FaMusic className="w-12 h-12 text-accent-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="release-form-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/admin/dashboard">
            <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="back-to-dashboard">
              <FaArrowLeft className="mr-2" /> Back
            </Button>
          </Link>
          <h1 className="text-xl font-syne font-bold text-white">
            {isEdit ? "Edit Release" : "New Release"}
          </h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="space-y-8"
        >
          {/* Auto-Fetch Section */}
          <div className="bg-gradient-to-r from-accent-primary/10 to-accent-secondary/10 border border-accent-primary/30 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-syne font-bold text-white flex items-center gap-2">
              <FaMagic className="text-accent-primary" /> Auto-Fetch Music Links
            </h2>
            <p className="text-sm text-zinc-400">
              Paste a Spotify, Apple Music, Deezer, or Tidal link to automatically fetch the title, artwork, and streaming links for all platforms.
            </p>
            <div className="flex gap-3">
              <Input
                value={fetchUrl}
                onChange={(e) => setFetchUrl(e.target.value)}
                placeholder="Paste a streaming link (Spotify, Apple Music, Deezer, Tidal)"
                className="flex-1 bg-zinc-900 border-zinc-700 text-white"
                data-testid="fetch-url-input"
              />
              <Button
                type="button"
                onClick={handleFetchLinks}
                disabled={fetching}
                className="bg-accent-primary hover:bg-accent-secondary rounded-full px-6"
                data-testid="fetch-links-btn"
              >
                {fetching ? (
                  <><FaSpinner className="animate-spin mr-2" /> Fetching...</>
                ) : (
                  <><FaMagic className="mr-2" /> Fetch</>
                )}
              </Button>
            </div>
          </div>

          {/* Tribe Integration Section */}
          <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border border-purple-500/30 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-syne font-bold text-white flex items-center gap-2">
              <FaUsers className="text-purple-400" /> Import from Tribe
            </h2>
            <p className="text-sm text-zinc-400">
              Select an artist from your Tribe app to import their social links, DSP links, and landing page.
            </p>
            {loadingTribeArtists ? (
              <div className="flex items-center gap-2 text-zinc-500">
                <FaSpinner className="animate-spin" /> Loading artists from Tribe...
              </div>
            ) : tribeArtists.length === 0 ? (
              <p className="text-zinc-500 text-sm">No artists found in Tribe. Check if the Tribe API is accessible.</p>
            ) : (
              <div className="flex gap-3">
                <Select value={selectedTribeArtist} onValueChange={setSelectedTribeArtist}>
                  <SelectTrigger className="flex-1 bg-zinc-900 border-zinc-700 text-white" data-testid="tribe-artist-select">
                    <SelectValue placeholder={`Select an artist (${tribeArtists.length} available)...`} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {tribeArtists.map((artist) => (
                      <SelectItem key={artist.id} value={artist.id} className="text-white hover:bg-zinc-800">
                        {artist.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleFetchFromTribe}
                  disabled={fetchingTribe || !selectedTribeArtist}
                  className="bg-purple-600 hover:bg-purple-700 rounded-full px-6"
                  data-testid="fetch-tribe-btn"
                >
                  {fetchingTribe ? (
                    <><FaSpinner className="animate-spin mr-2" /> Importing...</>
                  ) : (
                    <><FaUsers className="mr-2" /> Import</>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div className="bg-background-surface border border-zinc-800 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-syne font-bold text-white flex items-center gap-2">
              <FaMusic className="text-accent-primary" /> Basic Info
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Track Title"
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  required
                  data-testid="title-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Artist *</Label>
                <Input
                  value={formData.artist}
                  onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                  placeholder="Artist Name"
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  required
                  data-testid="artist-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400">Release Date *</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-zinc-900 border-zinc-700 text-white mt-1 hover:bg-zinc-800"
                    data-testid="date-picker-trigger"
                  >
                    <FaCalendar className="mr-2 h-4 w-4 text-zinc-400" />
                    {formData.release_date ? format(new Date(formData.release_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background-surface border-zinc-800" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.release_date ? new Date(formData.release_date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setFormData({ ...formData, release_date: date.toISOString().split("T")[0] });
                        setDatePickerOpen(false);
                      }
                    }}
                    initialFocus
                    className="bg-background-surface"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Artwork */}
          <div className="bg-background-surface border border-zinc-800 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-syne font-bold text-white flex items-center gap-2">
              <FaImage className="text-accent-primary" /> Artwork
            </h2>

            <div>
              <Label className="text-zinc-400">Artwork URL *</Label>
              <Input
                value={formData.artwork_url}
                onChange={(e) => setFormData({ ...formData, artwork_url: e.target.value })}
                placeholder="https://example.com/artwork.jpg"
                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                required
                data-testid="artwork-url-input"
              />
            </div>

            {formData.artwork_url && (
              <div className="mt-4">
                <p className="text-xs text-zinc-500 mb-2">Preview:</p>
                <img
                  src={formData.artwork_url}
                  alt="Artwork preview"
                  className="w-32 h-32 object-cover rounded-lg border border-zinc-700"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                  data-testid="artwork-preview"
                />
              </div>
            )}
          </div>

          {/* YouTube */}
          <div className="bg-background-surface border border-zinc-800 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-syne font-bold text-white flex items-center gap-2">
              <FaYoutube className="text-accent-primary" /> YouTube Video
            </h2>

            <div>
              <Label className="text-zinc-400">YouTube URL</Label>
              <Input
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                data-testid="youtube-url-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Paste the full YouTube video URL</p>
            </div>
          </div>

          {/* DSP Links */}
          <div className="bg-background-surface border border-zinc-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-syne font-bold text-white">Streaming Links</h2>
              <Button
                type="button"
                onClick={addDSPLink}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300"
                data-testid="add-dsp-link-btn"
              >
                <FaPlus className="mr-2" /> Add Link
              </Button>
            </div>

            {formData.dsp_links.length === 0 ? (
              <p className="text-zinc-500 text-sm">No streaming links added yet.</p>
            ) : (
              <div className="space-y-3" data-testid="dsp-links-list">
                {formData.dsp_links.map((link, index) => (
                  <div key={index} className="flex items-center gap-3" data-testid={`dsp-link-row-${index}`}>
                    <select
                      value={link.service_id}
                      onChange={(e) => updateDSPLink(index, "service_id", e.target.value)}
                      className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm"
                      data-testid={`dsp-service-select-${index}`}
                    >
                      {dspServices.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                    <Input
                      value={link.url}
                      onChange={(e) => updateDSPLink(index, "url", e.target.value)}
                      placeholder="https://..."
                      className="flex-1 bg-zinc-900 border-zinc-700 text-white"
                      data-testid={`dsp-url-input-${index}`}
                    />
                    <Button
                      type="button"
                      onClick={() => removeDSPLink(index)}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-red-500"
                      data-testid={`remove-dsp-link-${index}`}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Social Links */}
          <div className="bg-background-surface border border-zinc-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-syne font-bold text-white">Artist Socials</h2>
              <Button
                type="button"
                onClick={addSocialLink}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300"
                data-testid="add-social-link-btn"
              >
                <FaPlus className="mr-2" /> Add Link
              </Button>
            </div>

            {formData.social_links.length === 0 ? (
              <p className="text-zinc-500 text-sm">No social links added yet.</p>
            ) : (
              <div className="space-y-3" data-testid="social-links-list">
                {formData.social_links.map((link, index) => (
                  <div key={index} className="flex items-center gap-3" data-testid={`social-link-row-${index}`}>
                    <select
                      value={link.platform_id}
                      onChange={(e) => updateSocialLink(index, "platform_id", e.target.value)}
                      className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm"
                      data-testid={`social-platform-select-${index}`}
                    >
                      {socialPlatforms.map((platform) => (
                        <option key={platform.id} value={platform.id}>
                          {platform.name}
                        </option>
                      ))}
                    </select>
                    <Input
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, "url", e.target.value)}
                      placeholder="https://..."
                      className="flex-1 bg-zinc-900 border-zinc-700 text-white"
                      data-testid={`social-url-input-${index}`}
                    />
                    <Button
                      type="button"
                      onClick={() => removeSocialLink(index)}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-red-500"
                      data-testid={`remove-social-link-${index}`}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* External Links */}
          <div className="bg-background-surface border border-zinc-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-syne font-bold text-white flex items-center gap-2">
                <FaLink className="text-accent-primary" /> External Links
              </h2>
              <Button
                type="button"
                onClick={addExternalLink}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300"
                data-testid="add-external-link-btn"
              >
                <FaPlus className="mr-2" /> Add Link
              </Button>
            </div>
            <p className="text-xs text-zinc-500">Add links with custom section headers (e.g., Mailing List, Merch Store, Artist Page)</p>

            {formData.external_links.length === 0 ? (
              <p className="text-zinc-500 text-sm">No external links added yet.</p>
            ) : (
              <div className="space-y-4" data-testid="external-links-list">
                {formData.external_links.map((link, index) => (
                  <div key={index} className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800 space-y-3" data-testid={`external-link-row-${index}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-500 mb-1 block">Section Header</Label>
                        <Input
                          value={link.header || "More Links"}
                          onChange={(e) => updateExternalLink(index, "header", e.target.value)}
                          placeholder="e.g., Mailing List, Merch, Artist Page"
                          className="bg-zinc-900 border-zinc-700 text-white"
                          data-testid={`external-header-input-${index}`}
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={() => removeExternalLink(index)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-500 mt-5"
                        data-testid={`remove-external-link-${index}`}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-40">
                        <Label className="text-xs text-zinc-500 mb-1 block">Link Name</Label>
                        <Input
                          value={link.name}
                          onChange={(e) => updateExternalLink(index, "name", e.target.value)}
                          placeholder="e.g., Subscribe"
                          className="bg-zinc-900 border-zinc-700 text-white"
                          data-testid={`external-name-input-${index}`}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-500 mb-1 block">URL</Label>
                        <Input
                          value={link.url}
                          onChange={(e) => updateExternalLink(index, "url", e.target.value)}
                          placeholder="https://..."
                          className="bg-zinc-900 border-zinc-700 text-white"
                          data-testid={`external-url-input-${index}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex items-center justify-end gap-4">
            <Link to="/admin/dashboard">
              <Button type="button" variant="outline" className="border-zinc-700 text-zinc-300" data-testid="cancel-btn">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              className="bg-accent-primary hover:bg-accent-secondary rounded-full px-8"
              disabled={saving}
              data-testid="save-release-btn"
            >
              {saving ? "Saving..." : isEdit ? "Update Release" : "Create Release"}
            </Button>
          </div>
        </motion.form>
      </main>
    </div>
  );
}
